/**
 * ALTIORA · Sales OS · Kanban pipeline page (Sprint 1)
 *
 * Config-driven: stages, products and AI rules come from the backend
 * `/api/sales_os/config` document maintained by the founder.
 */
import { useEffect, useState, useCallback } from "react";
import {
  Sparkles, Plus, RefreshCw, AlertTriangle, Trash2, X, Wand2, Upload,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PageShell, PageHeader, Card, PrimaryButton, GhostButton, DangerButton,
  Modal, FormField, Input, Textarea, Select, Loader, EmptyState,
  KanbanBoard, Metric, Badge, Alert, Label,
} from "@/components/altiora/PremiumUI";


export default function SalesOS() {
  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState(null);
  const [products, setProducts] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sales_os/kanban");
      setBoard(data);
      setProducts(data.products || []);
    } catch (err) {
      toast.error("Impossible de charger le pipeline");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMove = async (dealId, toStage) => {
    // Optimistic move
    setBoard((prev) => {
      if (!prev) return prev;
      const columns = prev.columns.map((c) => ({ ...c, cards: [...c.cards] }));
      let moving = null;
      for (const c of columns) {
        const idx = c.cards.findIndex((k) => k.id === dealId);
        if (idx >= 0) { moving = c.cards.splice(idx, 1)[0]; break; }
      }
      const target = columns.find((c) => c.key === toStage);
      if (moving && target) target.cards.unshift(moving);
      return { ...prev, columns };
    });
    try {
      await api.post(`/sales_os/deals/${dealId}/move`, { to_stage: toStage });
      toast.success("Étape mise à jour");
      load();
    } catch (err) {
      toast.error("Échec du déplacement");
      load();
    }
  };

  const stats = board ? {
    open: board.columns.filter(c => !c.is_won && !c.is_lost)
                       .reduce((s, c) => s + (c.count || 0), 0),
    pipeline: board.columns.filter(c => !c.is_won && !c.is_lost)
                            .reduce((s, c) => s + (c.total_commission || 0), 0),
    won: board.columns.filter(c => c.is_won)
                       .reduce((s, c) => s + (c.total_commission || 0), 0),
  } : null;

  return (
    <PageShell testid="sales-os-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <PageHeader
          eyebrow="Sprint 1 · Cœur commercial"
          title="Sales OS · Pipeline"
          subtitle="Suivez chaque opportunité de la première rencontre à l'émission du contrat."
          testid="sales-os-header"
          actions={
            <>
              <GhostButton onClick={load} testid="sales-os-refresh">
                <RefreshCw size={13} /> Rafraîchir
              </GhostButton>
              <GhostButton onClick={() => setShowImport(true)} testid="sales-os-import">
                <Upload size={13} /> Importer CSV
              </GhostButton>
              <PrimaryButton onClick={() => setShowCreate(true)}
                             testid="sales-os-new-deal">
                <Plus size={13} /> Nouvelle opportunité
              </PrimaryButton>
            </>
          }
        />

        {loading && (
          <div className="py-16 flex justify-center"><Loader label="Chargement du pipeline" /></div>
        )}

        {!loading && board && (
          <>
            {stats && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                <Metric label="Opportunités ouvertes" value={stats.open}
                        testid="sales-os-metric-open" />
                <Metric label="Pipeline (commissions estimées)"
                        value={stats.pipeline.toLocaleString("fr-CA",
                          { style: "currency", currency: "CAD", maximumFractionDigits: 0 })}
                        accent="#C5A880" testid="sales-os-metric-pipeline" />
                <Metric label="Ventes émises · année"
                        value={stats.won.toLocaleString("fr-CA",
                          { style: "currency", currency: "CAD", maximumFractionDigits: 0 })}
                        accent="#16A34A" testid="sales-os-metric-won" />
              </div>
            )}

            {board.columns.every((c) => (c.cards || []).length === 0) ? (
              <Card className="p-0" testid="sales-os-empty-card">
                <EmptyState
                  icon={Sparkles}
                  title="Votre pipeline est vide"
                  description="Ajoutez votre première opportunité pour commencer à suivre vos ventes."
                  action={
                    <PrimaryButton onClick={() => setShowCreate(true)}
                                   testid="sales-os-empty-cta">
                      <Plus size={13} /> Créer une opportunité
                    </PrimaryButton>
                  }
                  testid="sales-os-empty"
                />
              </Card>
            ) : (
              <KanbanBoard
                columns={board.columns}
                onMove={handleMove}
                onCardClick={(card) => setDetailId(card.id)}
                testid="sales-os-board"
              />
            )}
          </>
        )}
      </div>

      {showCreate && (
        <CreateDealModal
          products={products}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {showImport && (
        <ImportCsvModal
          products={products}
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); load(); }}
        />
      )}

      {detailId && (
        <DealDetailModal
          dealId={detailId}
          products={products}
          stages={board?.columns || []}
          onClose={() => setDetailId(null)}
          onChanged={load}
        />
      )}
    </PageShell>
  );
}


// ══════════════════════════════════════════════════════════════════
// Create deal modal
// ══════════════════════════════════════════════════════════════════

function CreateDealModal({ products, onClose, onCreated }) {
  const [form, setForm] = useState({
    client_name: "", client_email: "", client_phone: "",
    product_key: products[0]?.key || "",
    premium_annual: "", probability: 30, notes: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.client_name.trim()) {
      toast.error("Le nom du client est requis"); return;
    }
    if (!form.product_key) {
      toast.error("Sélectionnez un produit"); return;
    }
    setSaving(true);
    try {
      await api.post("/sales_os/deals", {
        client_name: form.client_name.trim(),
        client_email: form.client_email.trim() || null,
        client_phone: form.client_phone.trim() || null,
        product_key: form.product_key,
        premium_annual: parseFloat(form.premium_annual) || 0,
        probability: parseInt(form.probability, 10) || 30,
        notes: form.notes.trim() || null,
      });
      toast.success("Opportunité créée");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur à la création");
    } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} testid="sales-os-create-modal"
           title="Nouvelle opportunité"
           description="Ajoutez un contact et le produit envisagé — les autres champs sont optionnels."
           footer={
             <>
               <GhostButton onClick={onClose} testid="sales-os-create-cancel">Annuler</GhostButton>
               <PrimaryButton onClick={submit} disabled={saving}
                              testid="sales-os-create-submit">
                 {saving ? "Enregistrement…" : "Créer"}
               </PrimaryButton>
             </>
           }>
      <FormField label="Nom du client" required testid="sales-os-create-client">
        {({ id }) => (
          <Input id={id} value={form.client_name}
                 onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                 placeholder="Ex. Marie Tremblay"
                 testid="sales-os-create-client-input" />
        )}
      </FormField>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField label="Courriel" testid="sales-os-create-email">
          {({ id }) => (
            <Input id={id} type="email" value={form.client_email}
                   onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                   testid="sales-os-create-email-input" />
          )}
        </FormField>
        <FormField label="Téléphone" testid="sales-os-create-phone">
          {({ id }) => (
            <Input id={id} value={form.client_phone}
                   onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                   testid="sales-os-create-phone-input" />
          )}
        </FormField>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField label="Produit" required testid="sales-os-create-product">
          {({ id }) => (
            <Select id={id} value={form.product_key}
                    onChange={(e) => setForm({ ...form, product_key: e.target.value })}
                    testid="sales-os-create-product-select">
              {products.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label="Prime annuelle ($)" testid="sales-os-create-premium">
          {({ id }) => (
            <Input id={id} type="number" step="1" min="0"
                   value={form.premium_annual}
                   onChange={(e) => setForm({ ...form, premium_annual: e.target.value })}
                   placeholder="Ex. 2400"
                   testid="sales-os-create-premium-input" />
          )}
        </FormField>
      </div>
      <FormField label="Probabilité (%)" testid="sales-os-create-proba"
                 description="Estimation subjective de conclusion.">
        {({ id }) => (
          <Input id={id} type="number" min="0" max="100" step="5"
                 value={form.probability}
                 onChange={(e) => setForm({ ...form, probability: e.target.value })}
                 testid="sales-os-create-proba-input" />
        )}
      </FormField>
      <FormField label="Notes internes" testid="sales-os-create-notes">
        {({ id }) => (
          <Textarea id={id} value={form.notes} rows={3}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Contexte, freins connus, prochain rdv…"
                    testid="sales-os-create-notes-input" />
        )}
      </FormField>
    </Modal>
  );
}


// ══════════════════════════════════════════════════════════════════
// Deal detail modal (view + AI analyze + delete)
// ══════════════════════════════════════════════════════════════════

function DealDetailModal({ dealId, products, stages, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sales_os/deals/${dealId}`);
      setData(data);
    } catch (err) {
      toast.error("Impossible de charger le détail");
    } finally { setLoading(false); }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data: res } = await api.post(`/sales_os/deals/${dealId}/analyze`);
      setData((prev) => prev ? { ...prev, deal: { ...prev.deal,
        ai_blockage: res.blockage, ai_next_action: res.next_action } } : prev);
      toast.success("Analyse IA mise à jour");
      onChanged?.();
    } catch (err) {
      toast.error("Analyse IA indisponible");
    } finally { setAnalyzing(false); }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer cette opportunité ? Cette action est irréversible.")) return;
    try {
      await api.delete(`/sales_os/deals/${dealId}`);
      toast.success("Opportunité supprimée");
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error("Suppression impossible");
    }
  };

  const product = products.find((p) => p.key === data?.deal?.product_key);
  const stage = stages.find((s) => s.key === data?.deal?.stage_key);

  return (
    <Modal open onClose={onClose} testid="sales-os-detail-modal" size="lg"
           title={data?.deal?.client_name || "Chargement…"}
           description={product ? `${product.label} · ${stage?.label || ""}` : ""}
           footer={
             <>
               <DangerButton onClick={remove} testid="sales-os-detail-delete">
                 <Trash2 size={13} /> Supprimer
               </DangerButton>
               <div className="flex-1" />
               <GhostButton onClick={onClose} testid="sales-os-detail-close">
                 <X size={13} /> Fermer
               </GhostButton>
             </>
           }>
      {loading && <div className="py-6"><Loader /></div>}
      {!loading && data?.deal && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Metric label="Prime annuelle" testid="sales-os-detail-premium"
                    value={(data.deal.premium_annual || 0).toLocaleString("fr-CA",
                      { style: "currency", currency: "CAD", maximumFractionDigits: 0 })} />
            <Metric label="Commission estimée" testid="sales-os-detail-commission"
                    value={(data.deal.expected_commission || 0).toLocaleString("fr-CA",
                      { style: "currency", currency: "CAD", maximumFractionDigits: 0 })} />
            <Metric label="Probabilité" testid="sales-os-detail-proba"
                    value={`${data.deal.probability}%`} />
            <Metric label="Statut" testid="sales-os-detail-status"
                    value={<Badge variant={data.deal.status === "won" ? "green"
                      : data.deal.status === "lost" ? "red" : "navy"}>
                      {data.deal.status === "won" ? "Gagnée"
                      : data.deal.status === "lost" ? "Perdue" : "Ouverte"}
                    </Badge>} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-widest font-semibold"
                   style={{ color: "#0B2545" }}>
                <Sparkles size={12} className="inline mr-1" /> Coach IA
              </div>
              <PrimaryButton onClick={analyze} disabled={analyzing}
                             testid="sales-os-detail-analyze">
                <Wand2 size={12} /> {analyzing ? "Analyse…" : "Analyser"}
              </PrimaryButton>
            </div>
            {(data.deal.ai_blockage || data.deal.ai_next_action) ? (
              <Alert variant="warning" title="Diagnostic IA"
                     testid="sales-os-detail-ai-alert">
                {data.deal.ai_blockage && (
                  <div className="mb-1"><strong>Blocage :</strong> {data.deal.ai_blockage}</div>
                )}
                {data.deal.ai_next_action && (
                  <div><strong>Prochaine action :</strong> {data.deal.ai_next_action}</div>
                )}
              </Alert>
            ) : (
              <div className="text-[12px] italic" style={{ color: "#94A3B8" }}>
                Aucune analyse pour le moment. Cliquez sur « Analyser » pour lancer le coach IA.
              </div>
            )}
          </div>

          {data.deal.notes && (
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold mb-1"
                   style={{ color: "#0B2545" }}>Notes</div>
              <div className="text-[12px] whitespace-pre-wrap"
                   style={{ color: "#334155" }}>{data.deal.notes}</div>
            </div>
          )}

          {data.transitions?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold mb-1"
                   style={{ color: "#0B2545" }}>Historique</div>
              <ul className="space-y-1 text-[11px]" style={{ color: "#334155" }}>
                {data.transitions.slice().reverse().map((t, i) => (
                  <li key={t.id || i} data-testid={`sales-os-detail-transition-${i}`}>
                    <span style={{ color: "#94A3B8" }}>
                      {new Date(t.created_at).toLocaleDateString("fr-CA")} ·
                    </span>{" "}
                    {t.from_stage ? `${t.from_stage} → ${t.to_stage}` : `Ouverture · ${t.to_stage}`}
                    {t.note && <span style={{ color: "#94A3B8" }}> — {t.note}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}


// ══════════════════════════════════════════════════════════════════
// Import CSV modal — parse client-side, POST rows to bulk_import
// ══════════════════════════════════════════════════════════════════

function ImportCsvModal({ products, onClose, onImported }) {
  const [defaultProduct, setDefaultProduct] = useState(products[0]?.key || "");
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [importing, setImporting] = useState(false);
  const [errors, setErrors] = useState([]);

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return [];
    // Detect delimiter (comma vs semicolon)
    const delim = lines[0].includes(";") && !lines[0].includes(",") ? ";" : ",";
    const header = lines[0].split(delim).map((h) => h.trim().toLowerCase());
    const nameIdx  = header.findIndex((h) => /^(nom|name|client|full.?name)/.test(h));
    const emailIdx = header.findIndex((h) => /(email|courriel|mail)/.test(h));
    const phoneIdx = header.findIndex((h) => /(phone|tele|tel)/.test(h));
    const premIdx  = header.findIndex((h) => /(prime|premium|amount|montant)/.test(h));
    return lines.slice(1).map((line) => {
      const cells = line.split(delim).map((c) => c.trim().replace(/^"|"$/g, ""));
      const name = nameIdx >= 0 ? cells[nameIdx] : cells[0];
      return {
        client_name: name || "",
        client_email: emailIdx >= 0 ? cells[emailIdx] : "",
        client_phone: phoneIdx >= 0 ? cells[phoneIdx] : "",
        premium_annual: premIdx >= 0 ? parseFloat(cells[premIdx].replace(/[^\d.]/g, "")) || 0 : 0,
      };
    }).filter((r) => r.client_name);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseCsv(String(reader.result || ""));
        setRows(parsed);
        setErrors([]);
      } catch (err) {
        toast.error("Fichier CSV invalide");
      }
    };
    reader.readAsText(f);
  };

  const submit = async () => {
    if (!rows.length) { toast.error("Aucune ligne à importer"); return; }
    if (!defaultProduct) { toast.error("Produit par défaut requis"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/sales_os/deals/bulk_import", {
        default_product_key: defaultProduct,
        rows,
      });
      toast.success(`Import réussi · ${data.created_count} contacts ajoutés`);
      if (data.error_count > 0) {
        setErrors(data.errors);
        toast.warning(`${data.error_count} lignes ignorées (voir détails)`);
      } else {
        onImported();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'import");
    } finally { setImporting(false); }
  };

  return (
    <Modal open onClose={onClose} testid="sales-os-import-modal" size="lg"
           title="Importer des contacts (CSV)"
           description="Colonnes acceptées : nom (obligatoire), email, téléphone, prime. Le produit par défaut sera appliqué à toutes les lignes."
           footer={
             <>
               <GhostButton onClick={onClose} testid="sales-os-import-cancel">Annuler</GhostButton>
               <PrimaryButton onClick={submit} disabled={importing || !rows.length}
                              testid="sales-os-import-submit">
                 {importing ? "Import en cours…" : `Importer ${rows.length} contacts`}
               </PrimaryButton>
             </>
           }>
      <FormField label="Produit par défaut" required testid="sales-os-import-product">
        {({ id }) => (
          <Select id={id} value={defaultProduct}
                  onChange={(e) => setDefaultProduct(e.target.value)}
                  testid="sales-os-import-product-select">
            {products.map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </Select>
        )}
      </FormField>
      <div className="mb-3">
        <Label testid="sales-os-import-file-label">Fichier CSV</Label>
        <input type="file" accept=".csv,text/csv" onChange={onFile}
               data-testid="sales-os-import-file"
               className="text-[12px] block w-full mt-1" />
        {fileName && (
          <div className="text-[11px] mt-1" style={{ color: "#94A3B8" }}>
            {fileName} · {rows.length} lignes détectées
          </div>
        )}
      </div>
      {rows.length > 0 && (
        <div className="rounded-lg overflow-hidden max-h-64 overflow-y-auto"
             style={{ border: "1px solid #C5A88033" }}
             data-testid="sales-os-import-preview">
          <table className="w-full text-[11px]">
            <thead style={{ background: "#FBF6EA" }}>
              <tr>
                <th className="px-2 py-1 text-left">Nom</th>
                <th className="px-2 py-1 text-left">Courriel</th>
                <th className="px-2 py-1 text-left">Téléphone</th>
                <th className="px-2 py-1 text-right">Prime</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 20).map((r, i) => (
                <tr key={i} style={{ borderTop: "1px solid #C5A88018" }}>
                  <td className="px-2 py-1">{r.client_name}</td>
                  <td className="px-2 py-1">{r.client_email}</td>
                  <td className="px-2 py-1">{r.client_phone}</td>
                  <td className="px-2 py-1 text-right">
                    {r.premium_annual ? `${r.premium_annual} $` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 20 && (
            <div className="text-[10px] italic p-2 text-center"
                 style={{ color: "#94A3B8" }}>
              … et {rows.length - 20} lignes de plus
            </div>
          )}
        </div>
      )}
      {errors.length > 0 && (
        <Alert variant="warning" title="Lignes ignorées"
               testid="sales-os-import-errors">
          <ul className="list-disc ml-4">
            {errors.slice(0, 5).map((e, i) => (
              <li key={i}>Ligne {e.row + 2} · {e.reason}</li>
            ))}
          </ul>
        </Alert>
      )}
    </Modal>
  );
}
